import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from "react-router-dom";
import { LoadingSpinner, TrainingDetailsHeader, useAuth } from '../components';
import { Card } from '../components/ui';
import { API_BASE_URL, LoadingSpinnerMeta } from '../data';
import { Training, TrainingContent, VolunteerTrainingPageDetailsMetaProps } from '../data/types';
import { AudioIcon, CheckIcon, ChevronLeftIcon, ClockIcon, DocumentIcon, ExternalLinkIcon, InteractiveIcon, ConstructionIcon, QuizIcon, VideoIcon } from '../icons';

type Props = {
    meta: VolunteerTrainingPageDetailsMetaProps
    adminMode?: boolean
}

const parseContent = (content: TrainingContent | string): TrainingContent | null => {
    if (typeof content === 'string') {
        try {
            return JSON.parse(content) as TrainingContent
        } catch {
            return null
        }
    }
    return content
}

const getModuleLink = (trainingId: number, moduleIndex: number, adminMode: boolean) => {
    return adminMode
        ? `/trainings/${trainingId}/module/${moduleIndex}`
        : `/volunteer/training/${trainingId}/module/${moduleIndex}`
}

const getModuleIcon = (type: string, className?: string) => {
    switch (type) {
        case 'video':
            return <VideoIcon className={className} />
        case 'document':
            return <DocumentIcon className={className} />
        case 'audio':
            return <AudioIcon className={className} />
        case 'quiz':
            return <QuizIcon className={className} />
        case 'interactive':
            return <InteractiveIcon className={className} />
        case 'external':
            return <ExternalLinkIcon className={className} />
        case 'coming_soon':
            return <ConstructionIcon className={className} />
        default:
            return <DocumentIcon className={className} />
    }
}

export const VolunteerTrainingDetails = ({ meta, adminMode = false }: Props) => {

    const { trainingId } = useParams();
    const { state } = useLocation();
    const { user } = useAuth();

    const [training, setTraining] = useState<Training | null>(state?.training || null);
    const [volunteerId] = useState(adminMode ? null : (state?.volunteerId || user?.volunteer_csid));
    const [loading, setLoading] = useState(!state?.training);
    const [error, setError] = useState<string | null>(null);

    const parsedContent = training ? parseContent(training.content) : null;
    const modules = parsedContent?.modules || [];
    const totalDuration = modules.reduce((sum, mod) => sum + (mod.duration || 0), 0);
    const completedModulesCount = training?.completed_modules?.length || 0;
    const allModulesCompleted = modules.length > 0 && completedModulesCount === modules.length;



    useEffect(() => {
        const fetchTraining = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch specific training with volunteer completion data
                const endpoint = volunteerId
                    ? `${API_BASE_URL}/volunteer/${volunteerId}/training/${trainingId}`
                    : `${API_BASE_URL}/training/${trainingId}`;

                const res = await fetch(endpoint, { credentials: 'include' });

                if (!res.ok) throw new Error("Failed to fetch training");

                const result = await res.json();
                let data = result.value || result;

                if (Array.isArray(data) && Array.isArray(data[0])) {
                    data = data[0];
                }

                setTraining(data);

            } catch (err) {
                setError(err instanceof Error ? err.message : "An error occurred.");
            } finally {
                setLoading(false);
            }
        };

        fetchTraining();
    }, [trainingId, volunteerId]);


    if (loading) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <LoadingSpinner meta={LoadingSpinnerMeta} />
            </div>
        );
    }



    if (error) {
        return (
            <div className="w-full h-screen flex items-center justify-center px-4">
                <div className="max-w-2xl w-full mx-auto">
                    <div className="bg-danger-100 dark:bg-danger-900
                                border border-danger-400 dark:border-danger-700
                                text-danger-700 dark:text-danger-200
                                px-6 py-4 rounded-lg shadow-md">
                        <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }


    if (!training) {
        return <div className="p-4">No training found.</div>;
    }


    return (
        <>
            <div className="flex-1 bg-gray-50 dark:bg-gray-900 pt-8 pb-8">
                <div className="container mx-auto px-4">

                    {/* BACK BUTTON */}
                    <div className="flex justify-between items-center mb-6">
                        <Link
                            to={training.policy ? "/" : (adminMode ? "/trainings" : "/volunteer/training")}
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            {training.policy ? "Back to Home" : (adminMode ? "Back to Manage Trainings" : meta.backToTrainingsButton)}
                        </Link>
                    </div>

                    {/* TRAINING HEADER */}
                    <TrainingDetailsHeader
                        training={training}
                        totalDuration={totalDuration}
                        moduleCount={modules.length}
                        meta={meta}
                    />

                    {/* MODULES SECTION */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                            {meta.modulesLabel}
                        </h2>

                        {modules.length === 0 ? (
                            <Card>
                                <div className="p-8 text-center">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        No modules available for this {training.policy ? 'policy' : 'training'}.
                                    </p>
                                </div>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {modules.map((module, index) => {
                                    const isCompleted = training.completed_modules?.includes(index) || false;

                                    return (
                                        <Link
                                            key={index}
                                            to={getModuleLink(training.id, index, adminMode)}
                                            className="block"
                                        >
                                            <Card className="hover:shadow-lg transition-shadow">
                                                <div className="p-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 text-primary-600 dark:text-primary-400">
                                                            {getModuleIcon(module.type, "w-5 h-5")}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                                    {meta.moduleNumberPrefix} {index + 1}
                                                                </span>
                                                                {!adminMode && isCompleted && (
                                                                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300">
                                                                        <CheckIcon /> Completed
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {module.title}
                                                            </p>
                                                            {module.description && (
                                                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                                                    {module.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {module.duration && (
                                                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                                                <ClockIcon className="w-4 h-4" />
                                                                <span className="text-xs whitespace-nowrap">
                                                                    {module.duration} {meta.minutesSuffix}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {module.questions && module.questions.length > 0 && (
                                                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                                                <QuizIcon className="w-4 h-4" />
                                                                <span className="text-xs whitespace-nowrap">
                                                                    {module.questions.length}<span className='hidden md:inline'> question{module.questions.length !== 1 ? 's' : ''}</span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* TRAINING STATUS */}
                    {adminMode ? (
                        <Card className="mt-8 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
                            <div className="p-4">
                                <p className="text-sm text-primary-700 dark:text-primary-300">
                                    <strong>Admin View:</strong> You can view and navigate all modules. Completion tracking is not available in admin mode.
                                </p>
                            </div>
                        </Card>
                    ) : training.completion_status === 1 ? (
                        <Card className="mt-8">
                            <div className="p-4 text-center">
                                <p className="text-success-600 dark:text-success-400 font-medium">
                                    {training.policy ? 'You have acknowledged this policy.' : meta.trainingCompletedMessage}
                                </p>
                            </div>
                        </Card>
                    ) : (
                        <div className="mt-8 p-4 bg-warn-50 dark:bg-warn-900/20 border border-warn-200 dark:border-warn-800 rounded-lg">
                            <p className="text-sm text-warn-700 dark:text-warn-300">
                                Complete all modules to mark this {training.policy ? 'policy' : 'training'} as complete.
                            </p>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};
